/**
 * MyWorkSummary — compact summary row showing the user's card stats:
 * total assigned, overdue, and due this week.
 *
 * Fetches from GET /v1/users/me/cards on mount and computes stats client-side.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { getMyCards, MyCardItem } from '../../api/my-cards';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface WorkStats {
  readonly total: number;
  readonly overdue: number;
  readonly dueThisWeek: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function computeStats(cards: MyCardItem[]): WorkStats {
  const now = new Date();
  const endOfWeek = new Date(now);
  endOfWeek.setDate(endOfWeek.getDate() + 7);
  endOfWeek.setHours(23, 59, 59, 999);

  let overdue = 0;
  let dueThisWeek = 0;

  for (const card of cards) {
    if (!card.dueDate) continue;
    const due = new Date(card.dueDate);
    if (due.getTime() < now.getTime()) {
      overdue++;
    } else if (due.getTime() <= endOfWeek.getTime()) {
      dueThisWeek++;
    }
  }

  return { total: cards.length, overdue, dueThisWeek };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function MyWorkSummary(): React.ReactElement {
  const [stats, setStats] = useState<WorkStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAllCards = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      // Fetch all pages to get complete card counts
      const allCards: MyCardItem[] = [];
      let cursor: string | undefined;

      do {
        const response = await getMyCards({ limit: 100, cursor });
        allCards.push(...response.data);
        cursor = response.pagination.nextCursor ?? undefined;
      } while (cursor);

      setStats(computeStats(allCards));
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load work summary';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllCards();
  }, [fetchAllCards]);

  if (isLoading) {
    return (
      <section aria-label="My work summary" className="mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="bg-card border border-border rounded-lg p-4 animate-pulse"
              aria-hidden="true"
            >
              <div className="h-8 w-12 bg-muted rounded mb-1" />
              <div className="h-3 w-20 bg-muted rounded" />
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section aria-label="My work summary" className="mb-6">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
          My Work
        </h3>
        <div className="bg-card border border-border rounded-lg p-4">
          <p className="text-sm text-muted-foreground">
            You don't have any tasks assigned yet. Cards assigned to you will show up here.
          </p>
        </div>
      </section>
    );
  }

  if (!stats) return <></>;

  if (stats.total === 0) {
    return (
      <section aria-label="My work summary" className="mb-6">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
          My Work
        </h3>
        <div className="bg-card border border-border rounded-lg p-4">
          <p className="text-sm text-muted-foreground">
            You don't have any tasks assigned. Cards assigned to you will show up here.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section aria-label="My work summary" className="mb-6">
      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
        My Work
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" role="list">
        <StatCard
          label="Assigned"
          value={stats.total}
          colorClass="text-foreground"
        />
        <StatCard
          label="Overdue"
          value={stats.overdue}
          colorClass="text-destructive"
        />
        <StatCard
          label="Due This Week"
          value={stats.dueThisWeek}
          colorClass="text-warning"
        />
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// StatCard
// ---------------------------------------------------------------------------

interface StatCardProps {
  readonly label: string;
  readonly value: number;
  readonly colorClass: string;
}

function StatCard({ label, value, colorClass }: StatCardProps): React.ReactElement {
  return (
    <div
      className="bg-card border border-border rounded-lg p-4"
      role="listitem"
      aria-label={`${label}: ${value}`}
    >
      <p className={`text-2xl font-bold ${colorClass}`}>{value}</p>
      <p className="text-xs text-muted-foreground uppercase tracking-wide mt-1">
        {label}
      </p>
    </div>
  );
}
